import { useQuery } from '@apollo/client';
import { FormatAlignJustify, ArrowRightAlt } from '@mui/icons-material';
import { Button, Dialog, ListItem, ListItemIcon } from '@mui/material';
import {
  ExtendedList,
  ExtendedListItemText,
  Widget,
  WidgetContent,
  WidgetFooter,
  WidgetHeader,
  WidgetIcon,
  useLanguageCode,
  ExtendedListItemButton,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { VirtualList } from '~/components/VirtualList/VirtualList';
import { useOpenClose } from '~/hooks';

import { NEWSROOM_WIDGET_QUERY } from './NewsroomWidget.gql';

const MAX_ITEMS = 4;

export const NewsroomWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'NewsroomWidget' });
  const { languageCode } = useLanguageCode();
  const [widgetRef, active] = useScrollIntoView({ hash: 'newsroom' });

  const [dialogProps, dialogOpen] = useOpenClose();

  const { data, loading } = useQuery(NEWSROOM_WIDGET_QUERY, {
    variables: {
      id: entityId,
      limit: MAX_ITEMS,
      locale: languageCode,
    },
  });

  const { nodes, totalCount } = data?.supplier.news || {};

  const hasMore = (totalCount || 0) > MAX_ITEMS;

  const renderItem = useMemo(
    () =>
      ({ item }: { item: any }) => {
        const { title, date, url, source } = item;
        return (
          <ListItem disablePadding>
            <ExtendedListItemButton component="a" target="_blank" href={url} rel="noreferrer">
              <ListItemIcon>
                <ArrowRightAlt />
              </ListItemIcon>
              <ExtendedListItemText
                primary={title}
                primaryLines={2}
                secondaryLines={1}
                caption={t('itemCaption', {
                  source: source.title,
                  date: new Date(date),
                })}
              />
            </ExtendedListItemButton>
          </ListItem>
        );
      },
    [t],
  );

  const isNodesEmpty = !nodes || nodes.length === 0;

  return (
    <>
      <Widget ref={widgetRef} active={active} data-intercom-target="NewsroomWidget">
        <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<FormatAlignJustify fontSize="small" />} />} />
        <WidgetContent>
          {!isNodesEmpty ? (
            <ExtendedList items={nodes} renderItem={renderItem} />
          ) : loading ? (
            <CircularProgressCentered sx={{ position: 'relative' }} />
          ) : (
            <PlaceholderWidget />
          )}
        </WidgetContent>
        <WidgetFooter>
          <Button variant="text" disabled={!hasMore || isNodesEmpty} onClick={dialogOpen}>
            {t('showAll', { count: totalCount || 0 })}
          </Button>
        </WidgetFooter>
      </Widget>
      <Dialog {...dialogProps}>
        <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<FormatAlignJustify fontSize="small" />} />} />
        <VirtualList
          itemSize={114}
          query={NEWSROOM_WIDGET_QUERY}
          queryOptions={{
            skip: !dialogProps.open,
            getNodesRoot: (data) => data?.supplier.news,
            variables: {
              id: entityId,
              limit: 1000,
            },
          }}
          renderItem={renderItem}
        />
      </Dialog>
    </>
  );
};
